import { Component } from "react";
import { SHEET_AUTO_CLOSE_MS, SHUFFLE_START_DELAY_MS } from "../constants";
import { AUDIO_MODE_PROPS } from "./audio-modes";
import { popupComponentDefaultProps } from "./default-props";
import ActionsFooter from "./actions-footer";
import DebugBar from "./debug-bar";
import EffectsSection from "./effects-section";
import PopupHeader from "./popup-header";
import Mood from "./mood";
import SkinSheet from "./skin-sheet";
import HelpSheet from "./help-sheet";
import FeedbackSheet from "./feedback-sheet";
import SlowControlSection from "./slow-control-section";
import {
  hasShuffleSyncPropsChanged,
  seedSliderTouchedKeys,
  sliderFillPct,
  sliderMidValue,
  sliderValueFromClientX,
} from "./tile-slider";

const TILE_VALUE_HOLD_MS = 300;
const TILE_VALUE_FADE_MS = 300;

class PopupComponent extends Component {
  constructor(props) {
    super(props);

    this.sliderTouched = {};
    this.tileDrag = null;
    this.suppressAdjustableClick = false;
    this.state = {
      mount: false,
      draggingTileKey: null,
      holdingTileKey: null,
      revealingTileKey: null,
      releasingTileKey: null,
      fillGrowingKeys: {},
      shuffleBusy: false,
      subscriptionStatusStep: 0,
      openSheet: null,
      contentPushHeight: null,
      moodScrimTop: null,
      moodScrimOpacity: 0,
    };
    this.contentPushRef = null;
    this.contentEffectsRef = null;
  }

  componentDidMount() {
    this.syncContentPushHeight();
    this.contentEffectsResizeObserver = new ResizeObserver(() =>
      this.syncContentPushHeight(),
    );
    if (this.contentEffectsRef) {
      this.contentEffectsResizeObserver.observe(this.contentEffectsRef);
    }
    setTimeout(() => this.setState({ mount: true }), 200);
    this.scheduleSubscriptionStatusRotation();
    this.seedSliderTouched(this.props);
  }

  componentDidUpdate(prevProps, prevState) {
    this.componentDidUpdateSheet(prevState);

    const statusChanged =
      prevProps.trialMode !== this.props.trialMode ||
      prevProps.remainingTrialDays !== this.props.remainingTrialDays ||
      prevProps.isPro !== this.props.isPro ||
      prevProps.usageAvailable !== this.props.usageAvailable ||
      prevProps.trialEnded !== this.props.trialEnded ||
      prevProps.usageHit !== this.props.usageHit;
    if (statusChanged) this.scheduleSubscriptionStatusRotation();

    AUDIO_MODE_PROPS.forEach(({ key, prop, isActive }) => {
      const wasActive = isActive ? isActive(prevProps) : prevProps[prop];
      const isActiveNow = isActive ? isActive(this.props) : this.props[prop];
      if (!wasActive && isActiveNow) {
        if (!this.state.fillGrowingKeys[key]) {
          this.startFillGrow(key);
        }
      } else if (wasActive && !isActiveNow) {
        this.clearFillGrow(key);
      }
    });

    if (hasShuffleSyncPropsChanged(prevProps, this.props)) {
      this.seedSliderTouched(this.props);
    }
  }

  componentDidUpdateSheet(prevState) {
    if (prevState.openSheet !== this.state.openSheet) {
      this.armSheetAutoClose();
    }
  }

  startFillGrow(key) {
    this.setState(
      (state) => ({
        fillGrowingKeys: { ...state.fillGrowingKeys, [key]: true },
      }),
      () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.clearFillGrow(key);
          });
        });
      },
    );
  }

  clearFillGrow(key) {
    if (!this.state.fillGrowingKeys[key]) return;
    this.setState((state) => {
      const fillGrowingKeys = { ...state.fillGrowingKeys };
      delete fillGrowingKeys[key];
      return { fillGrowingKeys };
    });
  }

  componentWillUnmount() {
    clearTimeout(this.subscriptionStatusTimer);
    clearTimeout(this.shuffleStartTimer);
    clearTimeout(this.releaseTileTimer);
    clearTimeout(this.sheetAutoCloseTimer);
    clearTimeout(this.sheetCloseFallbackTimer);
    this.contentEffectsResizeObserver?.disconnect();
    this.teardownMoodScroll();
    this.unmounted = true;
    this.tileDrag = null;
  }

  // Freeze .content__push height while Mood is open so the iframe host
  // does not resize; scroll reveals Mood instead of growing the panel.
  syncContentPushHeight() {
    if (this.state.openSheet) return;
    // getBoundingClientRect keeps the sub-pixel height; offsetHeight rounds
    // to an integer, so pinning it when a sheet opens nudged everything
    // below — including the footer — by up to a pixel.
    const height = this.contentEffectsRef?.getBoundingClientRect().height;
    if (height == null) return;
    const current = this.state.contentPushHeight;
    // Ignore sub-pixel jitter so the observer cannot thrash setState.
    if (current != null && Math.abs(current - height) < 0.5) return;
    this.setState({ contentPushHeight: height });
  }

  teardownMoodScroll() {
    const el = this.contentPushRef;
    clearTimeout(this.moodScrollFallbackTimer);
    this.moodScrollFallbackTimer = null;
    if (!el) return;
    if (this.onMoodScroll) {
      el.removeEventListener("scroll", this.onMoodScroll);
      this.onMoodScroll = null;
    }
    if (this.onMoodScrollEnd) {
      el.removeEventListener("scrollend", this.onMoodScrollEnd);
      this.onMoodScrollEnd = null;
    }
  }

  syncMoodScrimFromScroll() {
    if (this.unmounted) return;
    const el = this.contentPushRef;
    if (!el) return;

    const max = Math.max(0, el.scrollHeight - el.clientHeight);
    const progress =
      max <= 0
        ? this.state.openSheet
          ? 1
          : 0
        : Math.min(1, Math.max(0, el.scrollTop / max));

    const root = document.getElementById("Slowed");
    const mood = document.getElementById("mood-sheet");
    let top = this.state.moodScrimTop;
    if (root && mood && progress > 0) {
      top = Math.max(
        0,
        Math.round(
          mood.getBoundingClientRect().top - root.getBoundingClientRect().top,
        ),
      );
    } else if (progress <= 0) {
      top = null;
    }

    if (
      top !== this.state.moodScrimTop ||
      progress !== this.state.moodScrimOpacity
    ) {
      this.setState({ moodScrimTop: top, moodScrimOpacity: progress });
    }
  }

  // Idle sheets close themselves; any pointer/key activity inside restarts
  // the countdown so a sheet in use is never yanked away.
  armSheetAutoClose() {
    clearTimeout(this.sheetAutoCloseTimer);
    if (!this.state.openSheet) return;
    this.sheetAutoCloseTimer = setTimeout(() => {
      if (!this.unmounted && this.state.openSheet) this.setSheetOpen(null);
    }, SHEET_AUTO_CLOSE_MS);
  }

  // `kind` is one of "mood" | "skin" | "help", or null to close.
  setSheetOpen(kind) {
    this.teardownMoodScroll();
    clearTimeout(this.sheetAutoCloseTimer);

    const el = this.contentPushRef;
    if (!el) {
      this.setState({ openSheet: kind || null });
      return;
    }

    const runScroll = (targetTop, onDone) => {
      // Two frames: layout settles, then measure and animate as one unit.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.unmounted || !this.contentPushRef) return;
          const push = this.contentPushRef;
          const max = Math.max(0, push.scrollHeight - push.clientHeight);
          const top = Math.max(0, Math.min(targetTop, max));

          const finish = () => {
            if (this.onMoodScrollEnd !== finish) return;
            this.syncMoodScrimFromScroll();
            this.teardownMoodScroll();
            onDone?.();
          };

          this.onMoodScroll = () => this.syncMoodScrimFromScroll();
          this.onMoodScrollEnd = finish;
          push.addEventListener("scroll", this.onMoodScroll);
          push.addEventListener("scrollend", this.onMoodScrollEnd);
          this.moodScrollFallbackTimer = setTimeout(finish, 450);

          if (Math.abs(push.scrollTop - top) < 1) {
            finish();
            return;
          }
          push.scrollTo({ top, behavior: "smooth" });
        });
      });
    };

    if (!kind) {
      // Scroll up first while layout stays put, then clear open state. The
      // scroll is a nicety: if scrollend never lands the sheet must still
      // close, so a hard fallback clears the state either way.
      let closed = false;
      const close = () => {
        if (closed || this.unmounted) return;
        closed = true;
        clearTimeout(this.sheetCloseFallbackTimer);
        this.setState({ openSheet: null });
      };
      this.sheetCloseFallbackTimer = setTimeout(close, 600);
      runScroll(0, close);
      return;
    }

    this.setState({ openSheet: kind }, () => {
      runScroll(Number.POSITIVE_INFINITY);
    });
  }

  seedSliderTouched(props) {
    Object.assign(this.sliderTouched, seedSliderTouchedKeys(props));
  }

  clearTileValueRevealTimer() {
    clearTimeout(this.releaseTileTimer);
    this.releaseTileTimer = null;
  }

  startTileValueReveal(modeKey) {
    this.clearTileValueRevealTimer();
    this.setState({
      draggingTileKey: null,
      holdingTileKey: null,
      revealingTileKey: modeKey,
      releasingTileKey: null,
    });
    this.releaseTileTimer = setTimeout(() => {
      if (this.unmounted) return;
      this.setState({
        revealingTileKey: null,
        releasingTileKey: modeKey,
      });
      this.releaseTileTimer = setTimeout(() => {
        if (this.unmounted) return;
        this.setState({ releasingTileKey: null });
      }, TILE_VALUE_FADE_MS);
    }, TILE_VALUE_HOLD_MS);
  }

  finishTileInteraction(modeKey, { animateRelease = false } = {}) {
    this.tileDrag = null;
    this.clearTileValueRevealTimer();

    const shouldAnimate =
      animateRelease &&
      modeKey &&
      (this.state.holdingTileKey === modeKey ||
        this.state.draggingTileKey === modeKey ||
        this.state.revealingTileKey === modeKey);

    if (shouldAnimate) {
      this.startTileValueReveal(modeKey);
      return;
    }

    if (
      this.state.draggingTileKey ||
      this.state.holdingTileKey ||
      this.state.revealingTileKey ||
      this.state.releasingTileKey
    ) {
      this.setState({
        draggingTileKey: null,
        holdingTileKey: null,
        revealingTileKey: null,
        releasingTileKey: null,
      });
    }
  }

  updateTileSliderFromPointer(clientX, mode, el) {
    if (!mode.slider || !el) return;

    const value = sliderValueFromClientX(clientX, el, mode.slider);
    mode.slider.onChange(value);
    this.sliderTouched[mode.key] = true;
  }

  applyDefaultSliderIfNeeded(mode) {
    if (!mode.slider || this.sliderTouched[mode.key]) return;

    const defaultVal =
      mode.slider.defaultValue !== undefined
        ? mode.slider.defaultValue
        : sliderMidValue(mode.slider);
    mode.slider.onChange(defaultVal);
    this.sliderTouched[mode.key] = true;
  }

  onAdjustableTilePointerDown(event, mode) {
    if (this.props.isProFeaturesLocked) return;

    const wasActive = !!mode.active;

    this.clearTileValueRevealTimer();
    this.tileDrag = {
      key: mode.key,
      pointerId: event.pointerId,
      startX: event.clientX,
      dragging: false,
      el: event.currentTarget,
      slider: mode.slider,
      wasActive,
      isActive: wasActive,
      activatedOnDown: false,
    };

    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch (_error) {
      // Ignore capture failures in unsupported environments.
    }

    if (!wasActive) {
      // Prefer click position; mid only when there is no pointer.
      const setFromClick = mode.slider && !this.sliderTouched[mode.key];
      mode.onToggle();
      if (setFromClick) {
        this.updateTileSliderFromPointer(
          event.clientX,
          mode,
          event.currentTarget,
        );
      }
      this.tileDrag.isActive = true;
      this.tileDrag.activatedOnDown = true;
      this.setState(
        {
          holdingTileKey: mode.key,
          revealingTileKey: null,
          releasingTileKey: null,
          fillGrowingKeys: {
            ...this.state.fillGrowingKeys,
            [mode.key]: true,
          },
        },
        () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.clearFillGrow(mode.key);
            });
          });
        },
      );
      return;
    }

    this.setState({
      holdingTileKey: mode.key,
      revealingTileKey: null,
      releasingTileKey: null,
    });
  }

  onAdjustableTilePointerMove(event) {
    if (!this.tileDrag || event.pointerId !== this.tileDrag.pointerId) return;
    if (!this.tileDrag.isActive) return;

    if (Math.abs(event.clientX - this.tileDrag.startX) <= 4) return;

    if (!this.tileDrag.dragging) {
      this.tileDrag.dragging = true;
      this.clearFillGrow(this.tileDrag.key);
      if (this.state.draggingTileKey !== this.tileDrag.key) {
        this.setState({ draggingTileKey: this.tileDrag.key });
      }
    }

    this.updateTileSliderFromPointer(
      event.clientX,
      { key: this.tileDrag.key, slider: this.tileDrag.slider },
      this.tileDrag.el,
    );
  }

  onAdjustableTilePointerUp(event, mode) {
    if (!this.tileDrag || event.pointerId !== this.tileDrag.pointerId) return;

    const { dragging, wasActive, activatedOnDown } = this.tileDrag;
    const shouldAnimateRelease =
      (dragging || activatedOnDown) &&
      this.state.holdingTileKey === mode.key;

    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (_error) {
      // Ignore release failures in unsupported environments.
    }

    this.finishTileInteraction(mode.key, {
      animateRelease: shouldAnimateRelease,
    });

    this.suppressAdjustableClick = true;

    if (dragging) {
      // Empty end of the track turns the effect off.
      if (mode.active && sliderFillPct(mode.slider) <= 0) {
        mode.onToggle();
        delete this.sliderTouched[mode.key];
      }
      return;
    }

    if (wasActive) {
      mode.onToggle();
    }
  }

  onAdjustableTileClick(mode) {
    if (this.props.isProFeaturesLocked) {
      this.props.onUpgradeClick();
      return;
    }

    if (this.suppressAdjustableClick) {
      this.suppressAdjustableClick = false;
      return;
    }

    this.handleAdjustableTileTap(mode);
  }

  handleAdjustableTileTap(mode) {
    if (mode.active) {
      mode.onToggle();
      return;
    }

    this.applyDefaultSliderIfNeeded(mode);
    mode.onToggle();
    this.flashTileValue(mode.key);
  }

  flashTileValue(modeKey) {
    this.startTileValueReveal(modeKey);
  }

  onAdjustableTilePointerCancel(event) {
    if (!this.tileDrag || event.pointerId !== this.tileDrag.pointerId) return;

    const { key, dragging, activatedOnDown } = this.tileDrag;

    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (_error) {
      // Ignore capture failures in unsupported environments.
    }

    this.finishTileInteraction(key, {
      animateRelease:
        (dragging || activatedOnDown) && this.state.holdingTileKey === key,
    });
  }

  async runShuffle() {
    if (this.state.shuffleBusy) return;
    if (this.props.isProFeaturesLocked) {
      this.props.onUpgradeClick();
      return;
    }

    this.setState({ shuffleBusy: true });
    try {
      await new Promise((resolve) => {
        this.shuffleStartTimer = setTimeout(resolve, SHUFFLE_START_DELAY_MS);
      });
      return await this.props.onAIShuffle();
    } finally {
      clearTimeout(this.shuffleStartTimer);
      this.setState({ shuffleBusy: false });
    }
  }

  async runMood(mood) {
    if (this.state.shuffleBusy) return;
    if (this.props.isProFeaturesLocked) {
      this.props.onUpgradeClick();
      return;
    }

    this.setState({ shuffleBusy: true });
    try {
      return await this.props.onApplyMood?.(mood);
    } finally {
      this.setState({ shuffleBusy: false });
    }
  }

  async runClearMood() {
    if (this.state.shuffleBusy) return;
    if (this.props.isProFeaturesLocked) {
      this.props.onUpgradeClick();
      return;
    }

    this.setState({ shuffleBusy: true });
    try {
      return await this.props.onClearMood?.();
    } finally {
      this.setState({ shuffleBusy: false });
    }
  }

  scheduleSubscriptionStatusRotation() {
    clearTimeout(this.subscriptionStatusTimer);
    const {
      trialMode,
      remainingTrialDays,
      isPro,
      trialEnded,
      usageHit,
      usageRemaining,
    } = this.props;
    const hasTrial = trialMode && remainingTrialDays >= 0;
    // Usage mode rotates the same way once the trial is over but free
    // minutes remain.
    const hasUsage =
      !hasTrial &&
      trialEnded === true &&
      usageHit !== true &&
      Math.floor(Number(usageRemaining) || 0) > 0;
    const shouldRotate = !isPro && (hasTrial || hasUsage);
    if (!shouldRotate) {
      this.setState({ subscriptionStatusStep: 0 });
      return;
    }
    const dwell = [4500, 2400, 2400];
    let step = 0;
    const tick = () => {
      step = (step + 1) % 3;
      this.setState({ subscriptionStatusStep: step });
      this.subscriptionStatusTimer = setTimeout(tick, dwell[step]);
    };
    this.subscriptionStatusTimer = setTimeout(tick, 2000);
  }

  render() {
    const {
      slowControl,
      isSlowedEnabled,
      error,
      notSupported,
      musicProOnly,
      isHeaderVisible,
      isPro,
      trialMode,
      trialEnded,
      skin,
      remainingTrialDays,
      usageHit,
      isFeedbackEnabled,
      isRateAppEnabled,
      isProFeaturesLocked,
      contentApplying,
      billingLoading,
    } = this.props;

    const { openSheet, contentPushHeight, moodScrimTop, moodScrimOpacity } =
      this.state;
    const moodOpen = openSheet !== null;
    const { debugMode: debugModeEnabled } = this.props;
    const showMood =
      this.props.isMoodEnabled !== false &&
      !notSupported &&
      !musicProOnly;

    if (error) {
      return (
        <div className="popup">
          <div className="content">
            <p>
              {process.env.DEBUG === "true"
                ? error.message
                : "Something went wrong"}
            </p>
          </div>
        </div>
      );
    }

    const tileHandlers = {
      onAdjustablePointerDown: (event, mode) =>
        this.onAdjustableTilePointerDown(event, mode),
      onAdjustablePointerMove: (event) =>
        this.onAdjustableTilePointerMove(event),
      onAdjustablePointerUp: (event, mode) =>
        this.onAdjustableTilePointerUp(event, mode),
      onAdjustablePointerCancel: (event) =>
        this.onAdjustableTilePointerCancel(event),
      onAdjustableClick: (mode) => this.onAdjustableTileClick(mode),
    };

    const skinModeClass =
      {
        mono: "mono-mode",
        winamp: "winamp-mode",
        y2k: "y2k-mode",
        matrix: "matrix-mode",
      }[skin] ||
      null;
    const skinClassName = [skinModeClass, moodOpen ? "mood-open" : null]
      .filter(Boolean)
      .join(" ");

    return (
      <div id="Slowed" className={skinClassName || undefined}>
        <div
          className="mood-panel-scrim"
          aria-hidden="true"
          style={{
            opacity: moodScrimOpacity,
            visibility: moodScrimOpacity > 0.01 ? "visible" : "hidden",
            ...(moodScrimTop != null ? { top: `${moodScrimTop}px` } : null),
          }}
        />
        <div className={`popup ${this.state.mount ? "" : "mounting"}`}>
          {isHeaderVisible && (
            <PopupHeader
              isPro={isPro}
              trialMode={trialMode}
              trialEnded={trialEnded}
              remainingTrialDays={remainingTrialDays}
              usageHit={usageHit}
              usageRemaining={this.props.usageRemaining}
              subscriptionStatusStep={this.state.subscriptionStatusStep}
              notSupported={notSupported}
              musicProOnly={musicProOnly}
              isSlowedEnabled={isSlowedEnabled}
              contentApplying={contentApplying}
              mount={this.state.mount}
              billingLoading={billingLoading}
              upgradeError={this.props.upgradeError}
              onUpgradeClick={() => this.props.onUpgradeClick()}
              onManageSubscriptionClick={() =>
                this.props.onManageSubscriptionClick()
              }
              onDebugToggle={() =>
                this.props.onDebugModeChange?.(!debugModeEnabled)
              }
              onSlowedToggle={(value) => this.props.onSlowedToggle(value)}
              hideManagePlan={this.props.hideManagePlan}
            />
          )}

          <div
            className={`content${musicProOnly ? " content--disabled" : ""}`}
          >
            {musicProOnly ? (
              <div className="content__overlay content__overlay--music-pro">
                <p className="content__overlay-title">YouTube Music</p>
                <p className="content__overlay-text">
                  Slowing tracks on{" "}
                  <strong>music.youtube.com</strong> is included with Slowed Pro.
                </p>
                <button
                  type="button"
                  className="content__overlay-cta"
                  onClick={() => this.props.onUpgradeClick()}
                >
                  Unlock Pro
                </button>
              </div>
            ) : null}

            <div
              className={`content__push-shell${moodOpen ? " content__push-shell--mood-open" : ""}`}
            >
              <div
                className="content__push"
                ref={(el) => {
                  this.contentPushRef = el;
                }}
                style={
                  contentPushHeight != null
                    ? { height: contentPushHeight }
                    : undefined
                }
              >
                <div className="content__effects-frame">
                  <div
                    className={`content__effects${!isSlowedEnabled && !musicProOnly ? " content--disabled" : ""}${moodOpen ? " content__effects--dimmed" : ""}`}
                    ref={(el) => {
                      this.contentEffectsRef = el;
                    }}
                    {...(moodOpen ? { inert: true } : {})}
                  >
                    {!isSlowedEnabled && !musicProOnly && (
                      <div className="content__overlay" />
                    )}

                    <SlowControlSection
                      slowControl={slowControl}
                      onSlowControlChange={(value) =>
                        this.props.onSlowControlChange(value)
                      }
                      preservePitchMode={this.props.preservePitchMode}
                      onPreservePitchModeChange={(value) =>
                        this.props.onPreservePitchModeChange(value)
                      }
                    />

                    <EffectsSection
                      props={this.props}
                      tileState={this.state}
                      tileHandlers={tileHandlers}
                    />
                  </div>

                  {moodOpen && (
                    <button
                      type="button"
                      className="content__mood-backdrop"
                      aria-label="Close panel"
                      onClick={() => this.setSheetOpen(null)}
                    />
                  )}
                </div>

                <div
                  onPointerDown={() => this.armSheetAutoClose()}
                  onPointerMove={() => this.armSheetAutoClose()}
                  onKeyDown={() => this.armSheetAutoClose()}
                >
                {showMood && openSheet === "mood" && (
                  <Mood
                    open
                    onOpenChange={(open) => this.setSheetOpen(open ? "mood" : null)}
                    isProFeaturesLocked={isProFeaturesLocked}
                    trackName={this.props.trackName}
                    onUpgradeClick={() => this.props.onUpgradeClick()}
                    onGenerateMood={() => this.runShuffle()}
                    onApplyMood={(mood) => this.runMood(mood)}
                    onClearMood={() => this.runClearMood()}
                    onOpen={(metadata) => this.props.onMoodOpen(metadata)}
                    onLockedClick={(metadata) =>
                      this.props.onMoodLockedClick(metadata)
                    }
                  />
                )}

                {openSheet === "skin" && (
                  <SkinSheet
                    open
                    skin={skin}
                    isProFeaturesLocked={isProFeaturesLocked}
                    debugMode={debugModeEnabled}
                    onSkinChange={(value) => {
                      this.props.onSkinChange(value);
                      this.setSheetOpen(null);
                    }}
                    onUpgradeClick={() => this.props.onUpgradeClick()}
                  />
                )}

                {openSheet === "help" && (
                  <HelpSheet
                    open
                    isProFeaturesLocked={isProFeaturesLocked}
                    detachedPanel={this.props.detachedPanel}
                    onDetachedPanelChange={(value) =>
                      this.props.onDetachedPanelChange(value)
                    }
                    onResetClick={() => {
                      this.props.onResetControls();
                      this.setSheetOpen(null);
                    }}
                    onShortcutsClick={() => {
                      this.props.onCustomizeShortcut();
                      this.setSheetOpen(null);
                    }}
                    onFaqClick={() => {
                      this.props.onHelpClick();
                      this.setSheetOpen(null);
                    }}
                  />
                )}

                {openSheet === "feedback" && (
                  <FeedbackSheet
                    open
                    isFeedbackEnabled={isFeedbackEnabled}
                    isRateAppEnabled={isRateAppEnabled}
                    onFeedbackClick={() => {
                      this.props.onFeedbackClick();
                      this.setSheetOpen(null);
                    }}
                    onRateAppClick={() => {
                      this.props.onRateAppClick();
                      this.setSheetOpen(null);
                    }}
                  />
                )}
                </div>
              </div>
            </div>

            <ActionsFooter
              showMood={showMood}
              showUsage={
                !isPro &&
                !(trialMode && remainingTrialDays >= 0) &&
                trialEnded === true &&
                usageHit !== true &&
                Math.floor(Number(this.props.usageRemaining) || 0) > 0
              }
              onUsageClick={() => this.props.onUsageClick()}
              onMoodClick={() =>
                this.setSheetOpen(openSheet === "mood" ? null : "mood")
              }
              onSkinClick={() =>
                this.setSheetOpen(openSheet === "skin" ? null : "skin")
              }
              showFeedback={
                isFeedbackEnabled !== false || isRateAppEnabled !== false
              }
              onFeedbackClick={() =>
                this.setSheetOpen(openSheet === "feedback" ? null : "feedback")
              }
              onHelpClick={() =>
                this.setSheetOpen(openSheet === "help" ? null : "help")
              }
              onChangelogClick={() => this.props.onChangelogClick()}
            />
          </div>
        </div>

          {debugModeEnabled && (
            <DebugBar
              testMode={this.props.testMode}
              paymentHookId={this.props.paymentHookId}
              hookBusy={this.props.hookBusy}
              hookError={this.props.hookError}
              isPro={isPro}
              trialMode={trialMode}
              usageHit={this.props.usageHit}
              usageProgress={this.props.usageProgress}
              onTestModeChange={(value) => this.props.onTestModeChange(value)}
              onRefreshPaymentHookClick={() =>
                this.props.onRefreshPaymentHookClick()
              }
              onStartTrialModeClick={() => this.props.onStartTrialModeClick()}
              onUsingUsageClick={() => this.props.onUsingUsageClick()}
              onForceMaxUsageClick={() => this.props.onForceMaxUsageClick()}
              onUpgradeClick={() => this.props.onUpgradeClick()}
            />
          )}
      </div>
    );
  }
}

PopupComponent.defaultProps = popupComponentDefaultProps;

self.Slowed.Components.Popup = PopupComponent;

export default PopupComponent;
